/**
 * Admin Tenant Search Hook
 *
 * Provides search functionality for admin users to find and select tenants.
 * Uses the existing admin tenants endpoint with debounced search input.
 *
 * @fileoverview Custom hook for admin tenant search functionality
 *
 * @example
 * const {
 *   searchQuery,
 *   setSearchQuery,
 *   results,
 *   isLoading,
 *   error,
 *   clearSearch,
 * } = useAdminTenantSearch();
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { AdminTenant, PaginatedResponse } from '../types/admin';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * Return type for useAdminTenantSearch hook
 */
interface UseAdminTenantSearchReturn {
  /** Current search query string */
  searchQuery: string;
  /** Set the search query (triggers debounced search) */
  setSearchQuery: (query: string) => void;
  /** Search results from API */
  results: AdminTenant[];
  /** Whether a search is currently in progress */
  isLoading: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Clear search query and results */
  clearSearch: () => void;
  /** Whether there are results to display */
  hasResults: boolean;
}

/**
 * Debounce delay in milliseconds
 */
const DEBOUNCE_DELAY = 300;

/**
 * Minimum characters before triggering search
 */
const MIN_SEARCH_LENGTH = 2;

/**
 * Maximum results to fetch
 */
const MAX_RESULTS = 10;

/**
 * Hook for searching tenants as an admin user
 *
 * Features:
 * - Debounced search input (300ms delay)
 * - Uses existing GET /api/admin/tenants endpoint
 * - Minimum 2 character search
 * - Returns up to 10 results
 * - Error handling and loading states
 *
 * @returns Search state and methods
 */
export function useAdminTenantSearch(): UseAdminTenantSearchReturn {
  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });

  const [searchQuery, setSearchQueryState] = useState('');
  const [results, setResults] = useState<AdminTenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track the latest search query for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  /**
   * Fetch tenants from admin API
   */
  const fetchTenants = useCallback(
    async (query: string): Promise<void> => {
      if (!sessionRef.current?.access_token) {
        setError('Not authenticated');
        return;
      }

      if (query.length < MIN_SEARCH_LENGTH) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          search: query,
          limit: MAX_RESULTS.toString(),
          sortBy: 'name',
          sortOrder: 'asc',
        });

        let data: PaginatedResponse<AdminTenant>;
        try {
          data = await apiJson<PaginatedResponse<AdminTenant>>(
            `/api/admin/tenants?${params}`,
            {},
            sessionRef.current!.access_token
          );
        } catch (err) {
          const message = toUserMessage(err, "We couldn't complete the search. Please try again.");
          // Permission errors surface as 403s; preserve message
          throw new Error(message);
        }

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setResults(data.data);
        }
      } catch (err) {
        if (isMountedRef.current) {
          const errorMessage =
            toUserMessage(err, "We couldn't complete the search. Please try again.");
          setError(errorMessage);
          setResults([]);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  /**
   * Set search query with debouncing
   */
  const setSearchQuery = useCallback(
    (query: string): void => {
      setSearchQueryState(query);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Clear results if query is empty or too short
      if (query.length < MIN_SEARCH_LENGTH) {
        setResults([]);
        setError(null);
        return;
      }

      // Debounce the search
      searchTimeoutRef.current = setTimeout(() => {
        fetchTenants(query);
      }, DEBOUNCE_DELAY);
    },
    [fetchTenants]
  );

  /**
   * Clear search query and results
   */
  const clearSearch = useCallback((): void => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQueryState('');
    setResults([]);
    setError(null);
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    results,
    isLoading,
    error,
    clearSearch,
    hasResults: results.length > 0,
  };
}

export default useAdminTenantSearch;
